import React, { useCallback, useState } from "react";
import { PenaltyType } from "@prestosports/prestostats-core/Football";
import Grid from "@material-ui/core/Grid";
import { Box, TextField } from "@material-ui/core";
import debounce from "lodash/debounce";
import { PrimaryButton } from "@prestosports/commons-ui";
import Save32 from "@carbon/icons-react/lib/save/32";
import selectorStyles from "./styles/SelectorStyles";
import CardButton from "../CardButton";
import SubmitCancelListener from "../listeners/SubmitCancelListener";

const PenaltyTypeSelector = ({
	selected = {},
	onSelected,
	disableKeyListener,
	penalties: penaltiesProp
}) => {
	const classes = selectorStyles();
	const basePenalties = penaltiesProp || Object.values(PenaltyType);
	const hasCustomPenalty =
		selected.code?.length && !basePenalties.some((p) => p.code === selected.code);

	let penalties = basePenalties;
	if (hasCustomPenalty) {
		penalties = [...basePenalties, { code: selected.code, name: selected.name }];
	}
	penalties = penalties.sort((a, b) => a.name.localeCompare(b.name));

	const [filteredPenalties, setFilteredPenalties] = useState(penalties);
	const [penaltySearch, setPenaltySearch] = useState("");
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFilter = useCallback(debounce(setFilteredPenalties, 400), []);

	const getFilteredPenalties = (searchText) => {
		if (searchText) {
			const compareValue = searchText.toLocaleLowerCase();
			return penalties.filter((p) => {
				const code = p.code.toLowerCase();
				const name = p.name.toLowerCase();
				return code.includes(compareValue) || name.includes(compareValue);
			});
		}
		return penalties;
	};

	const canSubmit = penaltySearch.length || selected.code;

	const handlePenaltySearch = (e) => {
		setPenaltySearch(e.target.value);
		debouncedFilter(getFilteredPenalties(e.target.value));
	};

	const handleSubmit = () => {
		if (canSubmit) {
			const customPenalty = { code: penaltySearch, name: penaltySearch };
			const foundPenalty = penalties.find((p) =>
				penaltySearch.length
					? p.code.toLowerCase() === penaltySearch.toLocaleLowerCase()
					: p.code === selected.code
			);
			onSelected(foundPenalty || customPenalty);
		}
	};

	const getPenaltyText = ({ code, name }) => {
		if (code === name) {
			return <span>{name}</span>;
		}
		return (
			<span>
				<span className={classes.hotkey}>{code}</span> {name}
			</span>
		);
	};

	return (
		<>
			<Box mb={1} px={0.5} className={classes.penaltyFilterBox}>
				<TextField
					className={classes.penaltyFilterTextfield}
					autoFocus
					variant="filled"
					label="Filter Penalties"
					fullWidth
					value={penaltySearch}
					onChange={handlePenaltySearch}
					onKeyDown={({ keyCode }) => keyCode === 13 && handleSubmit()}
				/>
				<PrimaryButton
					label="Save"
					onClick={handleSubmit}
					disabled={!canSubmit}
					variant="contained"
					startIcon={<Save32 />}
				/>
			</Box>
			<Grid container spacing={1} justifyContent="flex-start">
				{filteredPenalties.map((p) => (
					<Grid item xs={12} sm={3} key={p.code}>
						<CardButton
							size="small"
							textAlign="center"
							text={getPenaltyText(p)}
							onClick={() => onSelected(p)}
							active={selected.code === p.code}
							id={p.name.toLowerCase().replace(" ", "-")}
						/>
					</Grid>
				))}
				{!disableKeyListener && (
					<SubmitCancelListener
						submitDisabled={!canSubmit}
						onSubmit={handleSubmit}
					/>
				)}
			</Grid>
		</>
	);
};

export default PenaltyTypeSelector;
